<template>
  <div class="effect-controls">
    <effect-controls-panel-header />
    <effects-carousel
    class="effect-controls__carousel"
    v-if="effectGroup.effects.length > 1"
    :effects="effectGroup.effects"
    :active-effect="selectedAvailableEffect.name"
    @select-effect="(effect) => emits('selectEffect', effect)"
    />
    <div class="effect-controls__container" >
      <div v-if="selectedAvailableEffect.config.isAudioReactive" class="effect-controls__row effect-controls__audio-reactivity">
        <span class="effect-controls__control-name">Audio reactive</span>
        <div class="effect-controls__toggle-wrapper">
          <toggle v-model:isOn="projectStore.selectedEffect.audioReactivityEnabled" />
        </div>
      </div>
      <div class="effect-controls__row effect-controls__intensity">
        <span class="effect-controls__control-name">Intensity</span>
        <div class="effect-controls__slider-wrapper">
          <!-- NOTE: For now we've decided that the slider will have a normalised 0 to 1 value
                    it is the responsibility of each effect to interpret what the value means.
                    It's also been decided that the slider will always have the same step value
                    and if the effect is stepped e.g. pixelation, it will pixelate based on thresholds.
                    There will be no visual cues for the steps in that situation and the slider will
                    always be "smooth"
          -->
          <slider :min="0" :max="1" :step="0.01" v-model="projectStore.selectedEffect.intensity" />
        </div>
      </div>
    </div>
    <div class="effect-controls__divider"></div>
    <div class="effect-controls__row effect-controls__layers">
      <span class="effect-controls__control-name">Apply to</span>
      <div class="effect-controls__checkbox-groups">
        <checkbox
          :disabled="layerDisabled(Layers.BACKGROUND)"
          @change="updateEffectLayer(Layers.BACKGROUND)"
          :checked="layerChecked(Layers.BACKGROUND)"
          text="Background"
        />
        <checkbox
          :disabled="layerDisabled(Layers.TEXT)"
          @change="updateEffectLayer(Layers.TEXT)"
          :checked="layerChecked(Layers.TEXT)"
          text="Text"
        />
        <checkbox
          :disabled="layerDisabled(Layers.LOGO)"
          @change="updateEffectLayer(Layers.LOGO)"
          :checked="layerChecked(Layers.LOGO)"
          text="Logo"
        />
        <checkbox
          :disabled="layerDisabled(Layers.STICKER)"
          @change="updateEffectLayer(Layers.STICKER)"
          :checked="layerChecked(Layers.STICKER)"
          text="Sticker"
        />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { AvailableEffect, EffectGroup } from '@/static/effectName'
import Slider from '@/components/common/Slider.vue'
import Toggle from '@/components/common/Toggle.vue'
import Checkbox from '@/components/common/Checkbox.vue'
import EffectControlsPanelHeader from '@/components/effect/EffectControlsPanelHeader.vue'
import useEffect from '@/composables/UseEffect'
import { Layers } from '@/static/Layers'
import { useProjectStore } from '@/store/project-store'
import EffectsCarousel from '@/components/effect/EffectsCarousel.vue'

interface Props {
  effectGroup: EffectGroup,
  selectedAvailableEffect: AvailableEffect
}

const { updateEffectLayer } = useEffect()
const projectStore = useProjectStore()

const props = defineProps<Props>()

const emits = defineEmits<{
  (event: 'selectEffect', effect: AvailableEffect): void
}>()

const layerDisabled = (layer: Layers) => { return props.selectedAvailableEffect.config.layers.indexOf(layer) === -1 }
const layerChecked = (layer: Layers) => { return projectStore.selectedEffect.appliedToLayer.indexOf(layer) !== -1 }

</script>

<style lang="scss">
.effect-controls {
  background-color: $black;
  overflow: hidden;
  border-radius: 12px 12px 0 0;
  color: $white;
  width: 408px;
  font-size: 16px;
  line-height: 16px;
  font-family: $font-regular;
  padding-bottom: 20px;
  position: absolute;
  bottom: 0;
  right: 0;

  > div {
    margin-top: 20px;
    margin-left: 20px;
    margin-right: 20px;

    &.effect-controls__carousel {
      margin-right: 0px;
      margin-left: 0px;
    }
  }

  &__row {
    display: flex;
    align-items: center;
  }

  &__divider {
    height: 1px;
    background-color: $grey-84;
  }

  &__intensity {
    min-width: 205px;
  }

  &__intensity, &__audio-reactivity {
    width: 100%;
    line-height: 24px;
  }

  &__toggle-wrapper {
    margin-left: 16px;
  }

  &__slider-wrapper {
    margin-left: 16px;
    width: 100%;
  }

  &__container {
    display: flex;
    flex-direction: row;
    gap: 24px;
    height: 24px;
  }

  &__control-name {
    color: $grey-50;
    font-weight: 500;
    font-size: 11px;
    line-height: 12px;
    font-family: $font-medium;
  }

  &__checkbox-groups {
    display: flex;
    padding-left: 12px;
    gap: 12px;
  }

  .slider {
    display: block;
    width: 100%;
    height: 4px;
    margin-right: 15px;

    &::-webkit-slider-thumb {
      width: 15px;
      height: 15px;
    }
  }
}
</style>
